import { operationFailureMessage } from "./operationFailure.mjs";
import { conversationKey } from "./conversationIdentity.mjs";
import { isConfirmedTextSend } from "./textSendConfirmation.mjs";

// UI transport attempts, NOT conversation history. No Firebase or provider calls.
// Only the original send callback may reach the existing durable server queue.
let owner = null;
let generation = 0;
let sequence = 0;
const records = new Map();
const senders = new Map();
const tails = new Map();
const listeners = new Set();
let snapshot = [];
const active = phase => ["waiting", "initial", "confirming"].includes(phase);
const storageKey = () => `arflow_text_attempts:${owner}`;
const storedFields = r => ({
  id:r.id, scope:r.scope, storeId:r.storeId, phone:r.phone, draft:r.draft,
  quotedId:r.quotedId, quote:r.quote, phase:r.phase, error:r.error,
  uncertain:r.uncertain, createdAt:r.createdAt,
});
function publish() {
  let storageError = false;
  if (owner != null && typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem(storageKey(), JSON.stringify([...records.values()]
        .filter(r => r.phase !== "accepted").map(storedFields)));
    } catch (_) { storageError = true; }
  }
  snapshot = [...records.values()].map(r => ({ ...storedFields(r), storageError }));
  listeners.forEach(fn => fn());
}
export const subscribeTextSendAttempts = fn => { listeners.add(fn); return () => listeners.delete(fn); };
export const getTextSendAttempts = () => snapshot;
export function setTextSendAttemptOwner(nextOwner) {
  const next = nextOwner == null ? null : String(nextOwner);
  if (next === owner) return;
  owner = next;
  generation++;
  records.forEach(r => clearTimeout(r.timer));
  records.clear();
  senders.clear();
  tails.clear();
  if (owner != null && typeof sessionStorage !== "undefined") {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey()) || "[]");
      for (const r of saved) {
        if (!r?.id || !r.scope || r.scope !== conversationKey(r.storeId, r.phone) || typeof r.draft !== "string") continue;
        records.set(r.id, { ...storedFields(r), phase:active(r.phase) ? "uncertain" : r.phase,
          uncertain:active(r.phase) || r.uncertain, restored:true });
      }
    } catch (_) { /* No usable prior UI state; never reconstruct chat from it. */ }
  }
  publish();
}
export function textSendAttemptScope(storeId, phone) {
  return conversationKey(storeId, phone);
}
export function attemptsForConversation(rows, storeId, phone) {
  const scope = textSendAttemptScope(storeId, phone);
  return scope ? rows.filter(r => r.scope === scope && r.phase !== "accepted") : [];
}
export function prioritizeTextSendFailures(items, rows, target = item => ({
  storeId:item.lojaAtual || item.storeId || item.lojaId, phone:item.phone || item.telefone,
})) {
  const failures = new Set(rows.filter(r => ["failed", "uncertain"].includes(r.phase)).map(r => r.scope));
  return [...items].sort((a,b) => {
    const x = target(a), y = target(b);
    return Number(failures.has(textSendAttemptScope(y.storeId,y.phone)))
      - Number(failures.has(textSendAttemptScope(x.storeId,x.phone)));
  });
}
export function prioritizeTextSendGroups(groups, rows) {
  const ranked = groups.map(g => ({ ...g, items:prioritizeTextSendFailures(g.items, rows) }));
  const failedScopes = new Set(rows.filter(r => ["failed", "uncertain"].includes(r.phase)).map(r => r.scope));
  const hasFailure = g => g.items.some(item => failedScopes.has(textSendAttemptScope(
    item.lojaAtual || item.storeId || item.lojaId, item.phone || item.telefone)));
  return ranked.sort((a,b) => Number(hasFailure(b)) - Number(hasFailure(a)));
}
export function bindTextSendAttemptSender(scope, send) {
  if (!scope) return;
  senders.set(scope, send);
  for (const r of records.values()) {
    if (r.scope === scope && r.retryRequested && !r.running) {
      r.retryRequested = false;
      retryTextSendAttempt(r.id);
    }
  }
}
function execute(r, initialWindowMs = 3000) {
  if (r.running) return r.running;
  const send = r.send || senders.get(r.scope);
  if (!send) { r.retryRequested = true; return null; }
  const requestGeneration = generation;
  const previous = tails.get(r.scope);
  r.phase = previous ? "waiting" : "initial";
  r.error = "";
  // Preserve submission order within one store+conversation, not across
  // unrelated conversations. Nothing here locks or clears the editor.
  r.running = (previous ? previous.catch(() => {}) : Promise.resolve()).then(() => {
    if (requestGeneration !== generation) return { ok:false, uncertain:true };
    r.phase = "initial";
    r.timer = setTimeout(() => {
      if (requestGeneration !== generation || r.phase !== "initial") return;
      r.phase = "confirming";
      publish();
    }, initialWindowMs);
    publish();
    return send({
      clientMsgId:r.id,
      shouldContinue:() => requestGeneration === generation,
    }, r);
  }).then(result => {
    if (requestGeneration !== generation) return result;
    const accepted = isConfirmedTextSend(result) && result.messageId === r.id;
    r.uncertain = !accepted && (result?.uncertain === true || result == null
      || typeof result?.ok !== "boolean" || result?.ok === true);
    r.phase = accepted ? "accepted" : r.uncertain ? "uncertain" : "failed";
    r.error = accepted ? "" : operationFailureMessage("message", result?.error, { uncertain:r.uncertain });
    return result;
  }, error => {
    if (requestGeneration === generation) {
      r.phase = "uncertain";
      r.uncertain = true;
      r.error = operationFailureMessage("message", error, { uncertain:true });
    }
    return { ok:false, uncertain:true };
  }).finally(() => {
    clearTimeout(r.timer);
    if (tails.get(r.scope) === r.running) tails.delete(r.scope);
    r.running = null;
    if (requestGeneration === generation) publish();
  });
  tails.set(r.scope, r.running);
  publish();
  return r.running;
}
export function startTextSendAttempt({ storeId, phone, scope, draft, quotedId=null, quote=null, send, initialWindowMs=3000 }) {
  const id = `crm_${Date.now()}_${++sequence}_${Math.random().toString(36).slice(2,10)}`;
  const r = {
    id, scope:scope || textSendAttemptScope(storeId, phone), storeId, phone, draft,
    quotedId, quote:quote ? {
      providerMessageId:quote.providerMessageId, waMessageId:quote.waMessageId,
      texto:quote.texto, caption:quote.caption, tipo:quote.tipo,
    } : null,
    send, createdAt:Date.now(), phase:"initial",
  };
  records.set(id, r);
  const promise = execute(r, initialWindowMs);
  return { id, promise };
}
export function retryTextSendAttempt(id) {
  const r = records.get(id);
  if (!r || r.phase === "accepted") return null;
  return execute(r);
}
export function resetTextSendAttemptsForTest() {
  generation++;
  records.forEach(r => clearTimeout(r.timer));
  records.clear(); senders.clear(); tails.clear(); owner = null;
  publish();
}